import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import { selectIsEditMode } from 'features/common/editModeSlice';
import dataValidationService from 'features/ImportData/dataValidationService';
import { CheckedRestaurantResponse } from 'features/Store/models';
import { useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FEATURE_DELIVERY_ID, displayError, handleServiceError } from 'utils';
import ErrorDataDetailModal from '../ErrorDataDetailModal/ErrorDataDetailModal';
import useGlobalModal from '../GlobalModal/useGlobalModal';
import './ErrorDataSummaryModal.scss';

export interface ErrorDataSummaryModalProps {
  errorFromFeatureId: number; // 3, 14, 16
  data: CheckedRestaurantResponse[];
  onClose?: (isDataChanged: boolean) => void;
  onDeliverySubmit?: () => void;
}

function ErrorDataSummaryModal({ errorFromFeatureId, data, onClose, onDeliverySubmit }: ErrorDataSummaryModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal, closeGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [errorSummaryData, setErrorSummaryData] = useState<CheckedRestaurantResponse[]>(data);
  const [showErrorDataDetailModal, setShowErrorDataDetailModal] = useState<boolean>(false);
  const [selectedCheckedRestaurantResponse, setSelectedCheckedRestaurantResponse] =
    useState<CheckedRestaurantResponse>();

  function handleClose() {
    onClose?.(isDataChanged);
    closeGlobalModal();
  }

  function handleDeliverySubmit() {
    onDeliverySubmit?.();
    handleClose();
  }

  function handleRefetchSummaryData() {
    (async () => {
      try {
        const restaurantIds = data.map((item) => item.id);
        const response = await dataValidationService.getErrorImportDataSummaryByRestaurants(restaurantIds);
        setErrorSummaryData(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleOpenErrorDataDetailModal(item: CheckedRestaurantResponse) {
    setShowErrorDataDetailModal(true);
    setSelectedCheckedRestaurantResponse(item);
  }

  function handleCloseErrorDataDetailModal(hasFixErrorData: boolean) {
    setShowErrorDataDetailModal(false);
    setSelectedCheckedRestaurantResponse(undefined);

    if (hasFixErrorData) {
      handleRefetchSummaryData();
      setIsDataChanged(true);
    }
  }

  function renderErrorRestaurantItem(item: CheckedRestaurantResponse) {
    let isShowButtonErrorDetail = false;
    if (item.total_unregister > 0) {
      isShowButtonErrorDetail = true;
    }
    // if (item.total_invalid_pos_code > 0) {
    //   isShowButtonErrorDetail = true;
    // }
    // if (item.total_menu_has_poscode_in_main_and_sub_menu > 0 || item.total_multiple_registered > 0) {
    //   isShowButtonErrorDetail = true;
    // }
    if (item.total_multiple_registered > 0) {
      isShowButtonErrorDetail = true;
    }
    if (item.total_poscode_used_in_multiple_menu_subtitle_menu > 0) {
      isShowButtonErrorDetail = true;
    }

    return (
      <div key={item.id} className="error-data-summary-item">
        <Row className="mb-2">
          <Col className="d-flex align-items-center">
            <strong>
              {`${t('ErrorDataSummaryModal.restaurant_cd')}: ${item.cd}, ${t(
                'ErrorDataSummaryModal.restaurant_name'
              )}: ${item.name}`}
            </strong>
          </Col>
          <Col className="text-end">
            {isShowButtonErrorDetail && (
              <Button onClick={handleOpenErrorDataDetailModal.bind(null, item)}>
                {t('ErrorDataSummaryModal.error_detail')}
              </Button>
            )}
          </Col>
        </Row>
        {!isShowButtonErrorDetail && <div>{t('ErrorDataSummaryModal.not_found_invalid_data')}</div>}
        <ul className="mb-0">
          {/* check conistency */}
          {!item.is_consistency && (
            <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_has_invalid_consistency')}`}</li>
          )}

          {/* pattern 1 */}
          {item.total_unregister > 0 && <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern1')}`}</li>}

          {/* pattern 2: remove from version 3.0 */}
          {/* {item.total_invalid_pos_code > 0 && (
            <li
              className="mb-2"
              dangerouslySetInnerHTML={{ __html: t('ErrorDataSummaryModal.warn_error_pattern2') }}
            ></li>
          )} */}

          {/* pattern 3 or 4 */}
          {/* {(item.total_menu_has_poscode_in_main_and_sub_menu > 0 || item.total_multiple_registered > 0) && (
            <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern3or4')}`}</li>
          )} */}
          {item.total_multiple_registered > 0 && (
            <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern3or4')}`}</li>
          )}

          {/* pattern 5 */}
          {item.total_poscode_used_in_multiple_menu_subtitle_menu > 0 && (
            <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern5')}`}</li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <>
      <Modal
        size="lg"
        dialogClassName="error-data-summary-modal"
        className="alert-modal"
        show
        backdrop="static"
        centered
        onHide={handleClose}
      >
        <Modal.Header className="bg-primary justify-content-center">
          <Modal.Title className="fs-6 text-white">{t('App.info')}</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          {errorSummaryData.map((item) => renderErrorRestaurantItem(item))}
        </Modal.Body>

        <Modal.Footer
          className={`${
            errorFromFeatureId === FEATURE_DELIVERY_ID ? 'justify-content-between' : 'justify-content-center'
          } ${isEditMode ? 'reedit-mode-wrapper' : ''}`}
        >
          <Button variant="light" onClick={handleClose}>
            {t('Alert_Modal.close')}
          </Button>
          {errorFromFeatureId === FEATURE_DELIVERY_ID && (
            <Button variant="primary" onClick={handleDeliverySubmit}>
              {t('ErrorDataSummaryModal.delivery_submit')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {showErrorDataDetailModal && selectedCheckedRestaurantResponse && (
        <ErrorDataDetailModal
          restaurantId={selectedCheckedRestaurantResponse.id}
          errorFromFeatureId={errorFromFeatureId}
          onClose={handleCloseErrorDataDetailModal}
        />
      )}
    </>
  );
}

export default ErrorDataSummaryModal;
